import React, { useState, useEffect, useMemo } from 'react';
import './Admin.css';

/* ──────────────────────────────────────────────
   Plan definitions (mirrors PricingPage)
────────────────────────────────────────────── */
const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    desc: 'For exploring reports',
    price: 'Free',
    priceSub: 'Forever free tier',
    credits: 50,
    maxUsers: 1,
    featured: false,
    features: [
      '50 credits / month',
      'Up to 4 report types',
      'Basic analytics',
      '1 user account',
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    desc: 'For growing teams',
    price: '$99',
    pricePeriod: '/mo',
    priceSub: '2,000 credits included',
    credits: 2000,
    maxUsers: 3,
    featured: true,
    features: [
      '2,000 credits / month',
      'All 4 report types',
      'Advanced analytics',
      'Up to 3 users',
      'Priority support',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    desc: 'For large organisations',
    price: 'Custom',
    priceSub: 'Unlimited credits available',
    credits: null,
    maxUsers: null,
    featured: false,
    features: [
      'Custom credit limits',
      'All report types + custom',
      'Custom integrations',
      'Unlimited users',
      'Dedicated support',
    ],
  },
];

/* ──────────────────────────────────────────────
   Helpers
────────────────────────────────────────────── */
function getToken() {
  try {
    const s = sessionStorage.getItem('supabase-auth-token');
    return s ? (JSON.parse(s).access_token ?? process.env.REACT_APP_SUPABASE_ANON_KEY) : process.env.REACT_APP_SUPABASE_ANON_KEY;
  } catch { return process.env.REACT_APP_SUPABASE_ANON_KEY; }
}

function apiFetch(path) {
  const url = process.env.REACT_APP_SUPABASE_URL;
  const key = process.env.REACT_APP_SUPABASE_ANON_KEY;
  return fetch(`${url}/rest/v1/${path}`, {
    headers: { apikey: key, Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
  });
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/* ──────────────────────────────────────────────
   Main Component
────────────────────────────────────────────── */
const BillingManagement = () => {
  const [tab, setTab]       = useState('overview');
  const [orgs, setOrgs]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await apiFetch('organizations?select=id,name,plan,credits_used,credits_limit,billing_status,created_at&order=created_at.desc');
        setOrgs(res.ok ? await res.json() : []);
      } catch { setOrgs([]); }
      finally { setLoading(false); setError(''); }
    };
    load();
  }, []);

  const stats = useMemo(() => {
    const active    = orgs.filter(o => o.billing_status !== 'past_due' && o.billing_status !== 'inactive');
    const pastDue   = orgs.filter(o => o.billing_status === 'past_due');
    const pro       = orgs.filter(o => o.plan === 'professional').length;
    const ent       = orgs.filter(o => o.plan === 'enterprise').length;
    const mrr       = pro * 99;
    return { total: orgs.length, active: active.length, pastDue: pastDue.length, mrr, pro, ent };
  }, [orgs]);

  const planCounts = useMemo(() => PLANS.map(p => ({
    ...p,
    count: orgs.filter(o => o.plan === p.id).length,
  })), [orgs]);

  return (
    <div className="adm-container">

      <div className="adm-page-header">
        <h1 className="adm-page-title"><i className="bi bi-credit-card" />Subscription &amp; Billing</h1>
        <p className="adm-page-subtitle">Monitor subscription plans, credit usage, and billing status across all tenant organizations.</p>
      </div>
      <hr className="adm-divider" />

      <div className="adm-stats-row">
        {[
          { label: 'Monthly Revenue',       value: `$${stats.mrr.toLocaleString()}`, icon: 'bi-currency-dollar', color: '#006B54' },
          { label: 'Active Subscriptions',  value: stats.active,                     icon: 'bi-check-circle',    color: '#15803d' },
          { label: 'Past Due',              value: stats.pastDue,                    icon: 'bi-exclamation-triangle', color: '#d97706' },
          { label: 'Total Tenants',         value: stats.total,                      icon: 'bi-buildings',        color: '#1d4ed8' },
        ].map(s => (
          <div key={s.label} className="adm-stat-card" style={{ borderTopColor: s.color }}>
            <div className="adm-stat-icon" style={{ background: s.color + '18', color: s.color }}>
              <i className={`bi ${s.icon}`} />
            </div>
            <div className="adm-stat-body">
              <div className="adm-stat-value">{loading ? '—' : s.value}</div>
              <div className="adm-stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="adm-tabs">
        {[
          { key: 'overview',       label: 'Overview',       icon: 'bi-grid' },
          { key: 'subscriptions',  label: 'Subscriptions',  icon: 'bi-receipt' },
          { key: 'plans',          label: 'Plans',          icon: 'bi-layers' },
        ].map(t => (
          <button key={t.key} className={`adm-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
            <i className={`bi ${t.icon}`} style={{ marginRight: 6 }} />{t.label}
          </button>
        ))}
      </div>

      {/* ── Overview ── */}
      {tab === 'overview' && (
        <div className="adm-grid-2">
          <div className="adm-card">
            <div className="adm-card-header">
              <span className="adm-card-title">Revenue by Plan</span>
            </div>
            <div className="adm-card-body--flush">
              <table className="adm-table">
                <thead>
                  <tr><th>Plan</th><th>Tenants</th><th>Unit Price</th><th>MRR</th></tr>
                </thead>
                <tbody>
                  {planCounts.map(p => (
                    <tr key={p.id}>
                      <td style={{ fontWeight: 600 }}>{p.name}</td>
                      <td>{loading ? '—' : p.count}</td>
                      <td style={{ color: '#64748b' }}>{p.price}{p.pricePeriod ?? ''}</td>
                      <td style={{ fontWeight: 600, color: '#006B54' }}>
                        {p.id === 'professional' ? `$${(p.count * 99).toLocaleString()}` : p.id === 'enterprise' ? 'Custom' : '$0'}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: '2px solid #e8edf2', fontWeight: 700 }}>
                    <td colSpan={3}>Total MRR</td>
                    <td style={{ color: '#006B54' }}>${loading ? '—' : stats.mrr.toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="adm-card">
            <div className="adm-card-header">
              <span className="adm-card-title">How Credits Work</span>
            </div>
            <div className="adm-card-body">
              <p style={{ fontSize: '0.85rem', color: '#4a5a54', marginBottom: 14, lineHeight: 1.6 }}>
                Every report type costs <strong>250 credits</strong>. Credits are included in your plan monthly and unused credits roll over.
              </p>
              <table className="adm-table">
                <thead>
                  <tr><th>Plan</th><th>Credits / mo</th><th>Reports / mo</th></tr>
                </thead>
                <tbody>
                  <tr><td>Starter</td><td>50</td><td>0.2 (testing)</td></tr>
                  <tr><td>Professional</td><td>2,000</td><td>~8 reports</td></tr>
                  <tr><td>Enterprise</td><td>Custom</td><td>Unlimited</td></tr>
                </tbody>
              </table>
              <p style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 12 }}>
                Additional credits available at <strong>$0.50 per credit</strong> anytime.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Subscriptions ── */}
      {tab === 'subscriptions' && (
        <div className="adm-card">
          <div className="adm-card-header">
            <span className="adm-card-title">Active Subscriptions</span>
          </div>
          <div className="adm-card-body--flush">
            {error && <div className="adm-alert adm-alert--danger" style={{ margin: 16 }}><i className="bi bi-exclamation-circle" />{error}</div>}
            {loading ? (
              <div className="adm-empty"><div className="adm-spinner" /><span className="adm-empty-text">Loading subscriptions…</span></div>
            ) : orgs.length === 0 ? (
              <div className="adm-empty">
                <i className="bi bi-receipt" />
                <span className="adm-empty-text">No subscription records found. Tenant organizations will appear here once created.</span>
              </div>
            ) : (
              <div className="adm-table-wrap">
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th>Organization</th>
                      <th>Plan</th>
                      <th>Credits Used</th>
                      <th>Credits Limit</th>
                      <th>Billing Status</th>
                      <th>Since</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orgs.map(o => {
                      const used  = o.credits_used  ?? 0;
                      const limit = o.credits_limit ?? (o.plan === 'professional' ? 2000 : o.plan === 'starter' ? 50 : null);
                      const pct   = limit ? Math.min(100, Math.round((used / limit) * 100)) : null;
                      const status = o.billing_status ?? 'active';

                      return (
                        <tr key={o.id}>
                          <td style={{ fontWeight: 600 }}>{o.name ?? o.id}</td>
                          <td>
                            <span className="adm-badge" style={
                              o.plan === 'enterprise' ? { background: '#f3e8ff', color: '#7c3aed', borderColor: '#e9d5ff' } :
                              o.plan === 'professional' ? { background: '#dbeafe', color: '#1d4ed8', borderColor: '#bfdbfe' } :
                              { background: '#f0fdf4', color: '#15803d', borderColor: '#bbf7d0' }
                            }>
                              {o.plan ? o.plan.charAt(0).toUpperCase() + o.plan.slice(1) : 'Starter'}
                            </span>
                          </td>
                          <td>{used.toLocaleString()}</td>
                          <td>
                            {limit ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ flex: 1, height: 6, background: '#e8edf2', borderRadius: 4, overflow: 'hidden' }}>
                                  <div style={{ width: `${pct}%`, height: '100%', background: pct > 85 ? '#b91c1c' : '#006B54', borderRadius: 4, transition: 'width 0.4s' }} />
                                </div>
                                <span style={{ fontSize: '0.75rem', color: '#64748b', whiteSpace: 'nowrap' }}>{limit.toLocaleString()}</span>
                              </div>
                            ) : '∞ Unlimited'}
                          </td>
                          <td>
                            <span className={`adm-badge adm-badge--${status === 'past_due' ? 'yellow' : status === 'inactive' ? 'red' : 'green'}`}>
                              {status === 'past_due' ? 'Past Due' : status === 'inactive' ? 'Inactive' : 'Active'}
                            </span>
                          </td>
                          <td style={{ color: '#64748b', fontSize: '0.82rem' }}>{formatDate(o.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Plans ── */}
      {tab === 'plans' && (
        <>
          <div className="adm-grid-3" style={{ marginBottom: 24 }}>
            {PLANS.map(p => (
              <div key={p.id} className={`adm-plan-card${p.featured ? ' adm-plan-card--featured' : ''}`}>
                {p.featured && <div className="adm-plan-badge">RECOMMENDED</div>}
                <div className="adm-plan-name">{p.name}</div>
                <div className="adm-plan-desc">{p.desc}</div>
                <div className="adm-plan-price">
                  {p.price}{p.pricePeriod && <span>{p.pricePeriod}</span>}
                </div>
                <p style={{ fontSize: '0.78rem', opacity: 0.7, margin: '4px 0 0' }}>{p.priceSub}</p>
                <hr className="adm-plan-divider" />
                <ul className="adm-plan-features">
                  {p.features.map((f, i) => (
                    <li key={i} className="adm-plan-feature">
                      <i className="bi bi-check-circle-fill" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: 8 }}>
                  {loading ? '—' : planCounts.find(x => x.id === p.id)?.count ?? 0} active tenant{planCounts.find(x => x.id === p.id)?.count !== 1 ? 's' : ''}
                </div>
              </div>
            ))}
          </div>

          <div className="adm-card">
            <div className="adm-card-header">
              <span className="adm-card-title">Credit Pricing</span>
            </div>
            <div className="adm-card-body">
              <p style={{ fontSize: '0.875rem', color: '#4a5a54', margin: '0 0 12px', lineHeight: 1.7 }}>
                All report types cost <strong>250 credits each</strong>. Tenants can purchase additional credits at any time.
              </p>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {[
                  { label: 'Corporate Reports',   cost: '250 credits' },
                  { label: 'Functional Reports',  cost: '250 credits' },
                  { label: 'Individual Reports',  cost: '250 credits' },
                  { label: 'Additional Credits',  cost: '$0.50 each' },
                ].map(item => (
                  <div key={item.label} style={{ background: '#f8faf9', border: '1px solid rgba(0,107,84,0.1)', borderRadius: 10, padding: '12px 18px', minWidth: 180 }}>
                    <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: 4 }}>{item.label}</div>
                    <div style={{ fontWeight: 700, color: '#006B54' }}>{item.cost}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  );
};

export default BillingManagement;
